import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/ui/multi-select';
import useGetPrompts from '@/hooks/queries/prompts/useGetPrompts';
import {
  useCreateVariables,
  useUpdateVariable,
} from '@/hooks/queries/variables/variables-v2';
import { CreateVariablePayload, Variable } from '@/types/variables';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollShadow,
  Select,
  SelectItem,
  Switch,
  Textarea,
} from '@nextui-org/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Mention,
  MentionsInput as ReactMentionsInput,
  SuggestionDataItem,
} from 'react-mentions';
import * as z from 'zod';

// Add new interfaces
interface FieldToCapture {
  name: string;
  definition: string;
}

// Constants for new dropdowns and options
const UPDATE_STRATEGIES = {
  merge: { display: 'Merge with existing', description: null },
  append: {
    display: 'Append to existing',
    description:
      'New information is added to the bottom of the existing information',
  },
  overwrite: {
    display: 'Replace previous value',
    description: 'Existing information is entirely replaced by new information',
  },
} as const;

const VARIABLE_TYPES = {
  text: { display: 'Text', props: {} },
  integer: { display: 'Number', props: {} },
  boolean: { display: 'Yes/No', props: {} },
  // date: { display: 'Date', props: {} },
  // datetime: { display: 'Date & Time', props: {} },
  float: { display: 'Decimal', props: {} },
  single_select: {
    display: 'Single Choice',
    props: { options: [] as string[] },
  },
  list: {
    display: 'Multi Select',
    props: { options: [] as string[] },
  },
  random_number: {
    display: 'Random Number',
    props: {
      min_value: { type: 'number' },
      max_value: { type: 'number' },
    },
  },
} as const;

type VariableType = keyof typeof VARIABLE_TYPES;
type UpdateStrategy = keyof typeof UPDATE_STRATEGIES;

// Update the BaseVariableProps type
type BaseVariableProps = {
  source: 'conversation' | 'user_profile';
  fields_to_capture: { name: string; definition: string }[];
  options: string[];
  processing_principles: string;
  update_strategy: 'merge' | 'append' | 'overwrite';
  custom_format_template: string;
  conversation_capture_prompt: string;
  is_private: boolean;
  prompts: number[];
  min_value?: number;
  max_value?: number;
};

const variableSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .regex(
        /^[a-z0-9_]+$/,
        'Name must contain only lowercase letters, numbers, and underscores (no spaces)'
      ),
    description: z.string(),
    type: z.enum([
      'text',
      'integer',
      'boolean',
      'date',
      'datetime',
      'float',
      'single_select',
      'list',
      'random_number',
    ] as const),
    type_display: z.string().optional(),
    props: z
      .object({
        source: z.enum(['conversation', 'user_profile'] as const),
        options: z.array(z.string()).optional(),
        processing_principles: z.string().optional(),
        update_strategy: z
          .enum(['merge', 'append', 'overwrite'] as const)
          .optional(),
        custom_format_template: z.string().optional(),
        conversation_capture_prompt: z.string().optional(),
        fields_to_capture: z
          .array(
            z.object({
              name: z.string(),
              definition: z.string(),
            })
          )
          .default([]),
        is_private: z.boolean().optional(),
        prompts: z.array(z.number()).optional(),
        min_value: z.number().optional(),
        max_value: z.number().optional(),
      })
      .passthrough()
      .refine(
        (props) => {
          if (props.source === 'conversation') {
            return (
              props.fields_to_capture && props.fields_to_capture.length > 0
            );
          }
          return true;
        },
        {
          message:
            'At least one field must be captured for conversation source',
          path: ['fields_to_capture'],
        }
      )
      // Scoped to conversation: the strategy picker and its instructions field
      // only render there, so enforcing it elsewhere blocks saves on an error
      // the form has no way to show or the user to fix.
      .refine(
        (props) => {
          if (
            props.source === 'conversation' &&
            props.update_strategy === 'merge'
          ) {
            return !!props.processing_principles?.trim();
          }
          return true;
        },
        {
          message:
            'Merge instructions are required when strategy is "Merge with existing"',
          path: ['processing_principles'],
        }
      ),
    visible: z.boolean().default(true),
    required: z.boolean().default(false),
    nullable: z.boolean().default(false),
    current_value: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (['single_select', 'list'].includes(data.type)) {
      if (!data.props.options || data.props.options.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one option is required',
          path: ['props', 'options'],
        });
      }
    }
    if (data.type === 'random_number') {
      if (data.props.min_value === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Minimum value is required.',
          path: ['props', 'min_value'],
        });
      }

      if (data.props.max_value === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Maximum value is required.',
          path: ['props', 'max_value'],
        });
      }

      if (
        data.props.min_value !== undefined &&
        data.props.max_value !== undefined &&
        data.props.max_value < data.props.min_value
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Maximum value must be greater than or equal to minimum value.',
          path: ['props', 'max_value'],
        });
      }
    }
  });

type VariableFormValues = z.infer<typeof variableSchema>;

interface AddDynamicVariablePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingVariable?: Variable | null;
}

// Add helper function to convert to MultiSelectOption
const toMultiSelectOption = (
  value: string | number,
  label: string
): MultiSelectOption => ({
  value: String(value),
  label,
});

// Separate static content into its own component
// const MultiValueButtons = memo(
//   ({ onEdit, onRemove }: { onEdit: () => void; onRemove: () => void }) => (
//     <>
//       <button
//         onClick={(e) => {
//           e.preventDefault();
//           e.stopPropagation();
//           onEdit();
//         }}
//         className="p-1 hover:bg-default-200 rounded-full"
//       >
//         <Icon icon="mdi:pencil" className="h-3.5 w-3.5" />
//       </button>
//       <button
//         onClick={(e) => {
//           e.preventDefault();
//           e.stopPropagation();
//           onRemove();
//         }}
//         className="p-1 hover:bg-danger/20 hover:text-danger rounded-full"
//       >
//         <Icon icon="mdi:close" className="h-3.5 w-3.5" />
//       </button>
//     </>
//   )
// );

// MultiValueButtons.displayName = 'MultiValueButtons';

// const CustomMultiValue = memo(
//   ({
//     data,
//     onEdit,
//     ...props
//   }: {
//     data: MultiSelectOption & { definition?: string };
//     onEdit: (value: string | number) => void;
//   } & any) => {
//     // Create stable callback references
//     const handleEdit = useCallback(() => {
//       onEdit(data.value);
//     }, [data.value, onEdit]);

//     const handleRemove = useCallback(() => {
//       props.removeProps.onClick();
//     }, [props.removeProps]);

//     return (
//       <div className="flex items-center gap-1 bg-default-100 rounded-md px-2 py-1 mr-1 my-1">
//         <span className="text-sm text-foreground">{data.label}</span>
//         <MultiValueButtons onEdit={handleEdit} onRemove={handleRemove} />
//       </div>
//     );
//   },
//   (prevProps, nextProps) => {
//     // Only compare the values we actually use
//     return (
//       prevProps.data.value === nextProps.data.value &&
//       prevProps.data.label === nextProps.data.label
//     );
//   }
// );

// Add custom components for MultiSelect

// Move these outside the component to prevent recreation
const createFieldOption = (field: {
  name: string;
  definition: string;
}): MultiSelectOption & { definition: string } => ({
  value: field.name,
  label: `${field.name} - ${field.definition}`,
  definition: field.definition,
});

interface FieldSuggestion extends SuggestionDataItem {
  name: string;
  definition: string;
}

const defaultMentionStyle = {
  backgroundColor: 'var(--nextui-colors-primary-100)',
  padding: '2px 4px',
  borderRadius: '4px',
  color: 'var(--nextui-colors-primary)',
};

const defaultStyle = {
  control: {
    fontSize: 14,
    fontFamily: 'monospace',
    minHeight: '80px',
  },
  input: {
    margin: 0,
    padding: '10px',
    border: '1px solid var(--nextui-colors-border)',
    borderRadius: '8px',
    fontSize: 14,
    fontFamily: 'monospace',
    width: '100%',
    minHeight: '80px',
  },
  suggestions: {
    list: {
      backgroundColor: 'var(--nextui-colors-content1)',
      border: '1px solid var(--nextui-colors-border)',
      borderRadius: '8px',
      fontSize: 14,
      maxHeight: '200px',
      overflow: 'auto',
    },
    item: {
      padding: '8px 12px',
      '&focused': {
        backgroundColor: 'var(--nextui-colors-default-100)',
      },
    },
  },
};

const CustomMentionsInput = memo(
  ({
    value,
    onChange,
    fields,
    errorMessage,
  }: {
    value: string;
    onChange: (value: string) => void;
    fields: { name: string; definition: string }[];
    errorMessage?: string;
    isInvalid?: boolean;
  }) => {
    // const handleFieldEdit = useCallback(
    //   (value: string | number) => {
    //     const field = (fields || []).find((f) => f.name === value);
    //     if (field) {
    //       // First set the editing field
    //       setEditingField({
    //         name: field.name,
    //         definition: field.definition || '',
    //       });
    //       // Then set the form values
    //       setTempFieldName(field.name);
    //       setTempFieldDefinition(field.definition || '');
    //     }
    //   },
    //   [fields]
    // );

    // const handleCancel = useCallback(() => {
    //   const currentEditingField = editingField; // Capture current value

    //   // First clear the editing state
    //   setEditingField(null);
    //   setTempFieldName('');
    //   setTempFieldDefinition('');

    //   // If we were editing, restore the original values
    //   if (currentEditingField) {
    //     setTempFieldName(currentEditingField.name);
    //     setTempFieldDefinition(currentEditingField.definition);
    //   }
    // }, [editingField]);

    const mentionData = useCallback(
      (query: string, callback: (data: FieldSuggestion[]) => void) => {
        const filtered = fields
          .map((field) => ({
            id: field.name,
            display: field.name,
            name: field.name,
            definition: field.definition,
          }))
          .filter(
            (field) =>
              field.name.toLowerCase().includes(query.toLowerCase()) ||
              field.definition.toLowerCase().includes(query.toLowerCase())
          );
        callback(filtered);
      },
      [fields]
    );

    const handleChange = useCallback(
      (event: { target: { value: string } }) => {
        // Clean up the format from @[name](name) to [name]
        const cleanedValue = event.target.value.replace(
          /@\[([^\]]+)\]\([^\)]+\)/g,
          '[$1]'
        );
        onChange(cleanedValue);
      },
      [onChange]
    );

    // Convert [name] format back to @[name](name) for the mentions input
    const displayValue = useMemo(() => {
      return value.replace(/\[([^\]]+)\]/g, '@[$1]($1)');
    }, [value]);

    return (
      <div className="relative">
        <div className="text-xs text-foreground-600 mb-2">Output template</div>
        <div className="relative border border-default-200 rounded-md">
          <ReactMentionsInput
            value={displayValue}
            onChange={handleChange}
            style={defaultStyle}
            placeholder="Enter format template (e.g., This is [full_name] and my date of birth is [date_of_birth])"
            // className={isInvalid ? 'border-danger' : 'bg-default-500'}
          >
            <Mention
              trigger="@"
              data={mentionData}
              displayTransform={(id) => `[${id}]`}
              markup="@[__display__](__id__)"
              style={defaultMentionStyle}
              renderSuggestion={(suggestion) => {
                const fieldSuggestion = suggestion as FieldSuggestion;
                return (
                  <ScrollShadow className="flex flex-col">
                    <span className="font-medium">
                      [{fieldSuggestion.name}]
                    </span>
                    <span className="text-sm text-foreground-500">
                      {fieldSuggestion.definition}
                    </span>
                  </ScrollShadow>
                );
              }}
            />
          </ReactMentionsInput>
        </div>
        {errorMessage ? (
          <div className="text-danger text-sm mt-1">{errorMessage}</div>
        ) : (
          <div className="text-xs text-default-600 mt-1 opacity-50">
            Mention field using @ and select from the dropdown
          </div>
        )}
      </div>
    );
  }
);

CustomMentionsInput.displayName = 'CustomMentionsInput';

export { CustomMentionsInput as MentionsInput };

// Update the Custom Format Template section to use the new validation
const validateMentionsTemplate = (
  template: string,
  fields: FieldToCapture[]
) => {
  // Match all @mentions that are followed by a word character
  const mentions = template.match(/\[([^\]]+)\]/g) || [];
  const mentionNames = mentions.map((m) => m.slice(1, -1)); // Remove @ symbol
  const fieldNames = fields.map((f) => f.name);

  const invalidVariables = mentionNames.filter(
    (name) => !fieldNames.includes(name)
  );

  return {
    invalidVariables,
    allVariables: mentionNames,
  };
};

export function AddDynamicVariablePanel({
  open,
  onOpenChange,
  editingVariable,
}: AddDynamicVariablePanelProps) {
  // Add local state for temporary inputs and error
  const [tempFieldName, setTempFieldName] = useState('');
  const [tempFieldDefinition, setTempFieldDefinition] = useState('');
  const [tempOption, setTempOption] = useState('');
  const [formatError, setFormatError] = useState('');

  // Add state to track the original field values when editing
  const [editingField, setEditingField] = useState<{
    name: string;
    definition: string;
  } | null>(null);

  const createVariables = useCreateVariables();
  const updateVariable = useUpdateVariable(editingVariable?.id || 0);
  const { data: promptsData } = useGetPrompts();
  const initialValuesRef = useRef<VariableFormValues | null>(null);

  // Update the defaultValues
  const defaultValues: VariableFormValues = {
    name: editingVariable?.name || '',
    description: editingVariable?.description || '',
    type: (editingVariable?.type as VariableType) || 'text',
    type_display: editingVariable?.type_display || '',
    props: {
      source: editingVariable?.props?.source || 'user_profile',
      fields_to_capture: editingVariable?.props?.fields_to_capture || [],
      options: editingVariable?.props?.options || [],
      processing_principles:
        editingVariable?.props?.processing_principles || '',
      update_strategy: editingVariable?.props?.update_strategy || 'overwrite',
      custom_format_template:
        editingVariable?.props?.custom_format_template || '',
      conversation_capture_prompt:
        editingVariable?.props?.conversation_capture_prompt || '',
      is_private: editingVariable?.props?.is_private || false,
      prompts:
        editingVariable?.props?.prompts?.map((id: number | string) =>
          Number(id)
        ) || [],
      min_value: editingVariable?.props?.min_value,
      max_value: editingVariable?.props?.max_value,
    },
    visible: editingVariable?.visible ?? true,
    required: editingVariable?.required || false,
    nullable: editingVariable?.nullable || false,
    current_value: editingVariable?.current_value || '',
  };

  // Update the values type to include the new fields
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<
    VariableFormValues & { newFieldName?: string; newFieldDefinition?: string }
  >({
    resolver: zodResolver(variableSchema),
    defaultValues,
  });
  const isFieldExists = useMemo(() => {
    return watch('props.fields_to_capture')?.find(
      (f) => f.name === tempFieldName
    );
  }, [watch('props.fields_to_capture'), tempFieldName]);

  // Reset form and store initial values when editingVariable changes
  useEffect(() => {
    if (editingVariable) {
      const values = {
        name: editingVariable.name,
        description: editingVariable.description,
        type: editingVariable.type as VariableType,
        type_display: editingVariable.type_display,
        // Older variables predate these flags; the schema demands booleans.
        visible: editingVariable.visible ?? true,
        required: editingVariable.required ?? false,
        nullable: editingVariable.nullable ?? false,
        current_value: editingVariable.current_value,
        props: {
          source: editingVariable.props?.source || 'user_profile',
          fields_to_capture: editingVariable.props?.fields_to_capture || [],
          options: editingVariable.props?.options || [],
          processing_principles:
            editingVariable.props?.processing_principles || '',
          update_strategy:
            editingVariable.props?.update_strategy || 'overwrite',
          custom_format_template:
            editingVariable.props?.custom_format_template || '',
          conversation_capture_prompt:
            editingVariable.props?.conversation_capture_prompt || '',
          is_private: editingVariable.props?.is_private || false,
          prompts:
            editingVariable.props?.prompts?.map((id: number | string) =>
              Number(id)
            ) || [],
          min_value: editingVariable?.props?.min_value,
          max_value: editingVariable?.props?.max_value,
        },
      };
      reset(values);
      initialValuesRef.current = values;
    } else {
      reset(defaultValues);
      initialValuesRef.current = defaultValues;
    }
  }, [editingVariable, reset]);

  // TODO: SOMEHOW inbuilt dirty fields is not working, so we are using this custom function to get dirty fields
  const getDirtyFields = (
    currentValues: VariableFormValues
  ): Partial<VariableFormValues> => {
    const dirtyFields: Partial<VariableFormValues> = {};
    const initial = initialValuesRef.current;

    if (!initial) return {};

    // Compare each field
    (Object.keys(currentValues) as Array<keyof VariableFormValues>).forEach(
      (key) => {
        if (key === 'props') {
          // Deep compare for props object
          if (
            JSON.stringify(currentValues[key]) !== JSON.stringify(initial[key])
          ) {
            dirtyFields[key] = currentValues[key];
          }
        } else if (currentValues[key] !== initial[key]) {
          dirtyFields[key] = currentValues[key];
        }
      }
    );

    // Always include type_display if type is dirty
    if (dirtyFields.type) {
      dirtyFields.type_display = currentValues.type_display;
    }

    return dirtyFields;
  };

  // Update the onSubmit function
  const onFormError = (formErrors: any) => {
    console.log(
      '[AddDynamicVariablePanel] Form validation errors:',
      formErrors
    );
    console.log(
      '[AddDynamicVariablePanel] Error fields:',
      Object.keys(formErrors).map((key) => ({
        field: key,
        type: formErrors[key]?.type,
        message: formErrors[key]?.message,
      }))
    );
  };

  const onSubmit = async (data: VariableFormValues) => {
    try {
      // Set the type_display based on the selected type
      const variableType =
        data.type in VARIABLE_TYPES
          ? VARIABLE_TYPES[data.type as VariableType]
          : undefined;
      if (variableType) {
        data.type_display = variableType.display;
      }

      // Ensure props has the correct shape based on source
      const baseProps: BaseVariableProps = {
        source: data.props.source,
        fields_to_capture: data.props.fields_to_capture || [],
        options: data.props.options || [],
        processing_principles:
          data.props.update_strategy === 'merge'
            ? data.props.processing_principles || ''
            : '',
        update_strategy: data.props.update_strategy || 'overwrite',
        custom_format_template: data.props.custom_format_template || '',
        conversation_capture_prompt:
          data.props.conversation_capture_prompt || '',
        is_private: data.props.is_private || false,
        prompts: data.props.prompts || [],
        ...(data.type === 'random_number'
          ? {
              min_value: data.props.min_value,
              max_value: data.props.max_value,
            }
          : {}),
      };

      const variableData = {
        ...data,
        description: data.description || '',
        props: baseProps,
        nullable: data.nullable || false,
      };

      if (editingVariable?.id) {
        const dirtyValues = getDirtyFields(variableData);
        if (Object.keys(dirtyValues).length > 0) {
          await updateVariable.mutateAsync(dirtyValues);
        }
      } else {
        const payload: CreateVariablePayload = {
          content_type: 'zenauth.zenuser',
          variables: [variableData],
        };
        await createVariables.mutateAsync(payload);
      }
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save variable:', error);
    }
  };

  const watchType = watch('type') as VariableType;
  const watchOptions = watch('props.options') || [];
  const needsOptions = watchType === 'single_select' || watchType === 'list';
  const isRandomNumber = watchType === 'random_number';

  const fieldsToCapture = watch('props.fields_to_capture');
  const fieldOptions = useMemo(
    () => (fieldsToCapture || []).map(createFieldOption),
    [fieldsToCapture]
  );

  const handleFieldEdit = useCallback(
    (value: string | number) => {
      const fields = watch('props.fields_to_capture') || [];
      const field = fields.find((f) => f.name === value);
      if (field) {
        setEditingField({
          name: field.name,
          definition: field.definition || '',
        });
        setTempFieldName(field.name);
        setTempFieldDefinition(field.definition || '');
      }
    },
    [watch]
  );

  const handleCancel = useCallback(() => {
    // Just clear everything
    setTempFieldName('');
    setTempFieldDefinition('');
    setEditingField(null);
  }, []); // No dependencies needed

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      hideCloseButton
      classNames={{
        wrapper: '!items-start !justify-end',
        base: '!m-0 rounded-medium h-[calc(100vh-64px)] w-full sm:w-[800px] max-w-[40vw]', // Increased from 600px to 800px
      }}
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: { duration: 0.3 },
          },
          exit: {
            x: 800, // Updated to match new width
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        },
      }}
    >
      <ModalContent className="absolute top-2 right-2 h-[calc(100vh-20px)]">
        {(onClose) => (
          <>
            <ModalHeader className="flex justify-between items-center px-6 py-2 border-b">
              <span className="text-large font-semibold">
                {editingVariable ? 'Edit User Variable' : 'Add User Variable'}
              </span>
              <Button isIconOnly variant="light" onPress={onClose}>
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </ModalHeader>

            <ModalBody className="overflow-hidden mt-2">
              <ScrollShadow className="h-[calc(100vh-250px)]">
                <form
                  onSubmit={handleSubmit(onSubmit, onFormError)}
                  className="space-y-6"
                >
                  {/* Source Card Selector */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <span className="text-sm text-foreground-400">
                        What kind of variable is this?
                      </span>
                      <div
                        className={`grid grid-cols-2 gap-3 ${editingVariable ? 'pointer-events-none opacity-60' : ''}`}
                      >
                        <button
                          type="button"
                          className={`flex flex-col items-start p-3 rounded-lg border-2 transition-colors text-left ${
                            watch('props.source') === 'user_profile'
                              ? 'border-primary bg-primary/5'
                              : 'border-default-200 hover:border-default-300'
                          }`}
                          onClick={() => {
                            setValue('props.source', 'user_profile');
                            setValue('props.processing_principles', '');
                            setValue('props.update_strategy', 'overwrite');
                            setValue('props.custom_format_template', undefined);
                            setValue(
                              'props.conversation_capture_prompt',
                              undefined
                            );
                            setValue('props.fields_to_capture', []);
                            setValue('visible', true);
                          }}
                        >
                          <span className="text-sm font-semibold">
                            Profile field
                          </span>
                          <span className="text-tiny text-foreground-400">
                            Set by admin or user
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`flex flex-col items-start p-3 rounded-lg border-2 transition-colors text-left ${
                            watch('props.source') === 'conversation'
                              ? 'border-primary bg-primary/5'
                              : 'border-default-200 hover:border-default-300'
                          }`}
                          onClick={() => {
                            setValue('props.source', 'conversation');
                            setValue('type', 'text');
                            setValue('visible', true);
                            setValue('required', false);
                          }}
                        >
                          <span className="text-sm font-semibold">
                            Conversation capture
                          </span>
                          <span className="text-tiny text-foreground-400">
                            Extracted from chats by AI
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* System key */}
                    <div className="space-y-2">
                      <span className="text-sm text-foreground-400">
                        System key
                      </span>
                      <Input
                        placeholder={
                          watch('props.source') === 'conversation'
                            ? 'e.g. soul_map_insights'
                            : 'e.g. division, office_location'
                        }
                        {...register('name')}
                        errorMessage={errors.name?.message}
                        isInvalid={!!errors.name}
                        value={watch('name')}
                      />
                      <div className="text-tiny text-foreground-400">
                        Lowercase, underscores only. Referenced in templates as
                        [key_name]
                      </div>
                    </div>

                    {/* Description / Display name */}
                    <div className="space-y-2">
                      <span className="text-sm text-foreground-400">
                        {watch('props.source') === 'conversation'
                          ? 'Description'
                          : 'Display name'}
                      </span>
                      <Textarea
                        placeholder={
                          watch('props.source') === 'conversation'
                            ? 'What does this variable capture?'
                            : 'e.g. Division, Office location'
                        }
                        {...register('description')}
                        errorMessage={errors.description?.message}
                        isInvalid={!!errors.description}
                        value={watch('description')}
                        classNames={{
                          base: 'min-h-[80px] resize-y',
                          input: 'resize-y min-h-[80px]',
                        }}
                        minRows={3}
                      />
                      <div className="text-tiny text-foreground-400">
                        {watch('props.source') === 'conversation'
                          ? "Helps other admins understand this variable's purpose"
                          : 'What the user sees in their profile'}
                      </div>
                    </div>
                  </div>

                  {/* Variable Configuration */}
                  <div className="space-y-4">
                    {watch('props.source') === 'user_profile' && (
                      <>
                        <div className="space-y-2">
                          <Select
                            label="Input type"
                            placeholder="Select a type"
                            selectedKeys={[watch('type')]}
                            onChange={(e) => {
                              setValue('type', e.target.value as VariableType);
                              setValue(
                                'type_display',
                                VARIABLE_TYPES[e.target.value as VariableType]
                                  .display
                              );
                              // Reset options if switching from a type that doesn't need them
                              if (
                                !['single_select', 'list'].includes(
                                  e.target.value
                                )
                              ) {
                                setValue('props.options', []);
                              }
                              if (e.target.value !== 'random_number') {
                                setValue('props.min_value', undefined);
                                setValue('props.max_value', undefined);
                              }
                            }}
                            errorMessage={errors.type?.message}
                            isInvalid={!!errors.type}
                          >
                            {Object.entries(VARIABLE_TYPES).map(
                              ([key, { display }]) => (
                                <SelectItem key={key} value={key}>
                                  {display}
                                </SelectItem>
                              )
                            )}
                          </Select>
                        </div>

                        {needsOptions && (
                          <div className="space-y-2">
                            <div>
                              <div className="flex flex-col gap-2">
                                <div className="flex flex-wrap gap-2 p-2 border-2 border-default-100 rounded-medium min-h-[40px] bg-default-100">
                                  <div className="flex flex-col gap-2">
                                    <label className="text-tiny text-default-600 font-normal">
                                      Options
                                      {['single_select', 'list'].includes(
                                        watch('type')
                                      ) && (
                                        <span className="text-danger ml-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                      {watchOptions.map((option, index) => (
                                        <Chip
                                          key={index}
                                          onClose={() => {
                                            const newOptions = [
                                              ...watchOptions,
                                            ];
                                            newOptions.splice(index, 1);
                                            setValue(
                                              'props.options',
                                              newOptions
                                            );
                                          }}
                                          variant="flat"
                                        >
                                          {option}
                                        </Chip>
                                      ))}
                                    </div>
                                    {watchOptions.length === 0 && (
                                      <div className="text-default-400 text-sm">
                                        No options added yet
                                      </div>
                                    )}
                                  </div>

                                  <Textarea
                                    type="text"
                                    size="sm"
                                    placeholder="Type and press Enter to add option"
                                    value={tempOption}
                                    onChange={(e) =>
                                      setTempOption(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const value = tempOption.trim();
                                        if (value) {
                                          setValue('props.options', [
                                            ...watchOptions,
                                            value,
                                          ]);
                                          setTempOption('');
                                        }
                                      }
                                    }}
                                    classNames={{
                                      base: 'min-h-[16px] resize-y',
                                      input:
                                        'resize-y min-h-[16px] bg-foreground-100',
                                    }}
                                    minRows={1}
                                  />
                                </div>
                              </div>
                              {errors.props?.options?.message && (
                                <div className="text-danger text-sm mt-1">
                                  {errors.props.options.message.toString()}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {isRandomNumber && (
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              label="Min value"
                              type="number"
                              {...register('props.min_value', {
                                setValueAs: (value) =>
                                  value === '' || value === null
                                    ? undefined
                                    : Number(value),
                              })}
                              errorMessage={errors.props?.min_value?.message?.toString()}
                              isInvalid={!!errors.props?.min_value}
                            />

                            <Input
                              label="Max value"
                              type="number"
                              {...register('props.max_value', {
                                setValueAs: (value) =>
                                  value === '' || value === null
                                    ? undefined
                                    : Number(value),
                              })}
                              errorMessage={errors.props?.max_value?.message?.toString()}
                              isInvalid={!!errors.props?.max_value}
                            />
                          </div>
                        )}

                        {/* Behavior */}
                        <div className="space-y-4 border-t pt-4">
                          <h4 className="text-sm font-semibold text-foreground">
                            Behavior
                          </h4>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col flex-grow">
                              <span className="text-sm font-medium">
                                Required
                              </span>
                              <span className="text-tiny text-foreground-400">
                                User must fill this in before proceeding
                              </span>
                            </div>
                            <Switch
                              isSelected={watch('required')}
                              onValueChange={(value) =>
                                setValue('required', value)
                              }
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col flex-grow">
                              <span className="text-sm font-medium">
                                Visible
                              </span>
                              <span className="text-tiny text-foreground-400">
                                Show this field to users on their profile
                              </span>
                            </div>
                            <Switch
                              isSelected={watch('visible') ?? true}
                              onValueChange={(value) =>
                                setValue('visible', value, {
                                  shouldDirty: true,
                                })
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Conversation-specific Configuration */}
                  {watch('props.source') === 'conversation' && (
                    <>
                      {/* Section: Capture configuration */}
                      <div className="space-y-4 border-t pt-4">
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            Capture configuration
                          </h4>
                          <p className="text-tiny text-foreground-400">
                            Tell the AI what to extract from conversations when
                            they end
                          </p>
                        </div>

                        {/* Overall capture instructions */}
                        <div className="space-y-2">
                          <span className="text-sm text-foreground-400">
                            Overall capture instructions
                          </span>
                          <Textarea
                            placeholder="Instructions for the AI on what to extract..."
                            value={
                              watch('props.conversation_capture_prompt') || ''
                            }
                            onChange={(e) =>
                              setValue(
                                'props.conversation_capture_prompt',
                                e.target.value
                              )
                            }
                            isInvalid={
                              !!errors.props?.conversation_capture_prompt
                            }
                            errorMessage={
                              errors.props?.conversation_capture_prompt?.message
                            }
                            classNames={{
                              base: 'min-h-[120px] resize-y',
                              input: 'resize-y min-h-[120px]',
                            }}
                            minRows={4}
                          />
                          <div className="text-tiny text-foreground-400">
                            Gives the AI context about what its overall task is
                            and about the conversation transcript it is reading
                          </div>
                        </div>

                        {/* Data fields to extract */}
                        <div className="space-y-2">
                          <label className="block text-sm mb-1.5">
                            Data fields to extract
                          </label>

                          {/* Table-like display of fields */}
                          {fieldOptions.length > 0 && (
                            <div className="border border-default-200 rounded-lg overflow-hidden">
                              {/* Table header */}
                              <div className="grid grid-cols-[1fr_2fr_auto] gap-2 px-3 py-2 bg-default-50 border-b border-default-200">
                                <span className="text-xs text-foreground-400 uppercase tracking-wide font-medium">
                                  Field key
                                </span>
                                <span className="text-xs text-foreground-400 uppercase tracking-wide font-medium">
                                  What to look for
                                </span>
                                <span className="w-16" />
                              </div>
                              {/* Table rows */}
                              {fieldOptions.map((field, index) => {
                                const fieldData = (
                                  watch('props.fields_to_capture') || []
                                ).find((f) => f.name === field.value);
                                return (
                                  <div
                                    key={field.value}
                                    className={`grid grid-cols-[1fr_2fr_auto] gap-2 px-3 py-2 items-center ${
                                      index < fieldOptions.length - 1
                                        ? 'border-b border-default-100'
                                        : ''
                                    }`}
                                  >
                                    <span className="text-sm font-mono truncate">
                                      {field.value}
                                    </span>
                                    <span className="text-sm text-foreground-500 truncate">
                                      {fieldData?.definition || ''}
                                    </span>
                                    <div className="flex gap-1 w-16 justify-end">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleFieldEdit(field.value);
                                        }}
                                        className="p-1 hover:bg-default-200 rounded-full"
                                      >
                                        <Icon
                                          icon="mdi:pencil"
                                          className="h-3.5 w-3.5"
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const currentFields =
                                            watch('props.fields_to_capture') ||
                                            [];
                                          setValue(
                                            'props.fields_to_capture',
                                            currentFields.filter(
                                              (f) => f.name !== field.value
                                            )
                                          );
                                        }}
                                        className="p-1 hover:bg-danger/20 hover:text-danger rounded-full"
                                      >
                                        <Icon
                                          icon="mdi:close"
                                          className="h-3.5 w-3.5"
                                        />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {errors.props?.fields_to_capture && (
                            <div className="text-danger text-sm mt-1">
                              {errors.props.fields_to_capture.message}
                            </div>
                          )}

                          {/* Add field inputs */}
                          <div className="space-y-2">
                            <div className="flex gap-2 mb-2 items-start">
                              <Input
                                placeholder="field_key"
                                value={tempFieldName}
                                onChange={(e) =>
                                  setTempFieldName(e.target.value)
                                }
                                className="flex-1"
                                size="sm"
                                classNames={{
                                  input: 'font-mono',
                                }}
                              />
                              <Input
                                placeholder="What to look for..."
                                value={tempFieldDefinition}
                                onChange={(e) =>
                                  setTempFieldDefinition(e.target.value)
                                }
                                className="flex-1"
                                size="sm"
                              />
                              <div className="flex gap-1">
                                {isFieldExists || editingField ? (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        if (
                                          tempFieldName &&
                                          tempFieldDefinition
                                        ) {
                                          const currentFields =
                                            watch('props.fields_to_capture') ||
                                            [];
                                          setValue(
                                            'props.fields_to_capture',
                                            currentFields.map((f) =>
                                              f.name === tempFieldName
                                                ? {
                                                    ...f,
                                                    definition:
                                                      tempFieldDefinition,
                                                  }
                                                : f
                                            )
                                          );
                                          setTempFieldName('');
                                          setTempFieldDefinition('');
                                          setEditingField(null);
                                        }
                                      }}
                                    >
                                      Update
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="danger"
                                      variant="light"
                                      onClick={handleCancel}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="light"
                                    color="primary"
                                    startContent={
                                      <Icon
                                        icon="mdi:plus"
                                        className="h-4 w-4"
                                      />
                                    }
                                    onClick={() => {
                                      if (
                                        tempFieldName &&
                                        tempFieldDefinition
                                      ) {
                                        const currentFields =
                                          watch('props.fields_to_capture') ||
                                          [];
                                        setValue('props.fields_to_capture', [
                                          ...currentFields,
                                          {
                                            name: tempFieldName,
                                            definition: tempFieldDefinition,
                                          },
                                        ]);
                                        setTempFieldName('');
                                        setTempFieldDefinition('');
                                        setEditingField(null);
                                      }
                                    }}
                                  >
                                    Add
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Output template */}
                        <div className="space-y-2">
                          <CustomMentionsInput
                            value={watch('props.custom_format_template') || ''}
                            onChange={(value) => {
                              const fields =
                                watch('props.fields_to_capture') || [];
                              const { invalidVariables, allVariables } =
                                validateMentionsTemplate(value, fields);

                              if (invalidVariables.length > 0) {
                                setFormatError(
                                  `Invalid field(s): ${invalidVariables.join(', ')}. Please add these fields first or use only defined fields.`
                                );
                              } else if (
                                allVariables.length === 0 &&
                                value.trim() !== ''
                              ) {
                                setFormatError(
                                  'Template should include at least one field using @field format.'
                                );
                              } else {
                                setFormatError('');
                              }
                              setValue('props.custom_format_template', value);
                            }}
                            fields={watch('props.fields_to_capture') || []}
                            errorMessage={formatError}
                            isInvalid={!!formatError}
                          />
                          <div className="text-tiny text-foreground-400">
                            How field values are assembled and injected into
                            agent prompts. Reference fields with [field_key].
                          </div>
                        </div>
                      </div>

                      {/* Section: Scope and behavior */}
                      <div className="space-y-4 border-t pt-4">
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            Scope and behavior
                          </h4>
                          <p className="text-tiny text-foreground-400">
                            Control which agents trigger capture and how values
                            are stored
                          </p>
                        </div>

                        {/* Capture from these agents */}
                        <div className="space-y-2">
                          <span className="text-sm text-foreground-400">
                            Capture from these agents
                          </span>
                          <MultiSelect
                            placeholder="Select agents..."
                            value={
                              (watch('props.prompts')
                                ?.map((id) => {
                                  const prompt = promptsData?.results.find(
                                    (p) => p.id === id
                                  );
                                  return prompt
                                    ? toMultiSelectOption(
                                        prompt.id,
                                        prompt.name
                                      )
                                    : undefined;
                                })
                                .filter(Boolean) as MultiSelectOption[]) || []
                            }
                            options={
                              promptsData?.results.map((prompt) =>
                                toMultiSelectOption(prompt.id, prompt.name)
                              ) || []
                            }
                            onChange={(selectedOptions) => {
                              setValue(
                                'props.prompts',
                                selectedOptions.map((option) =>
                                  Number(option.value)
                                )
                              );
                            }}
                          />
                          <div className="text-tiny text-foreground-400">
                            Only conversations with these agents will trigger
                            capture
                          </div>
                        </div>

                        {/* When values change */}
                        <div className="space-y-2">
                          <Select
                            label="When values change"
                            placeholder="Select strategy"
                            selectedKeys={[
                              watch('props.update_strategy') || 'overwrite',
                            ]}
                            onChange={(e) => {
                              setValue(
                                'props.update_strategy',
                                e.target.value.toLowerCase() as UpdateStrategy
                              );
                            }}
                            isInvalid={!!errors.props?.update_strategy}
                            errorMessage={
                              errors.props?.update_strategy?.message
                            }
                          >
                            {Object.entries(UPDATE_STRATEGIES).map(
                              ([key, { display, description }]) => (
                                <SelectItem key={key} textValue={display}>
                                  <div className="flex flex-col gap-0">
                                    <span>{display}</span>
                                    {description && (
                                      <span className="text-tiny text-foreground-400">
                                        {description}
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              )
                            )}
                          </Select>
                          <div className="text-tiny text-foreground-400">
                            Each new capture replaces or adds to the stored
                            value
                          </div>
                        </div>

                        {watch('props.update_strategy') === 'merge' && (
                          <div className="space-y-2">
                            <span className="text-sm text-foreground-400">
                              Merge instructions{' '}
                              <span className="text-danger">*</span>
                            </span>
                            <Textarea
                              placeholder="e.g., Keep the most recent medication list. Preserve allergies across updates. Remove outdated symptoms."
                              value={watch('props.processing_principles') || ''}
                              onChange={(e) =>
                                setValue(
                                  'props.processing_principles',
                                  e.target.value,
                                  { shouldDirty: true }
                                )
                              }
                              isInvalid={!!errors.props?.processing_principles}
                              errorMessage={
                                errors.props?.processing_principles?.message
                              }
                              classNames={{
                                base: 'min-h-[96px] resize-y',
                                input: 'resize-y min-h-[96px]',
                              }}
                              minRows={3}
                            />
                            <div className="text-tiny text-foreground-400">
                              Guides the AI on how to merge new information with
                              the existing value of this variable
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </form>
              </ScrollShadow>
            </ModalBody>

            <ModalFooter className="justify-between border-t mt-4">
              <Button variant="bordered" onPress={onClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit(onSubmit, onFormError)}
                isLoading={
                  createVariables.isPending || updateVariable.isPending
                }
              >
                {createVariables.isPending || updateVariable.isPending
                  ? 'Saving...'
                  : editingVariable
                    ? 'Save changes'
                    : 'Create variable'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
